import dayjs, { type Dayjs } from "dayjs";
import { inviteAppointmentId, isInviteExpired } from "../../invites/helpers/inviteTaskHelpers";
import { teleconsultoriaGuestOfAppointment } from "../../utils/actors";

/**
 * Situação da reserva para a afiliada:
 * - accepted: teleconsultor aceitou (está no Appointment como CON);
 * - pending: convite aguardando resposta;
 * - rejected / expired / cancelled: convite recusado, vencido ou cancelado.
 */
export type BookingInviteStatus = "accepted" | "pending" | "rejected" | "expired" | "cancelled";

export interface BookingInviteState {
  status: BookingInviteStatus;
  label: string;
  /** Nome do teleconsultor: CON do Appointment ou destinatário do convite. */
  teleconsultorName: string;
  /** Convite mais recente da reserva (para cancelar junto, se ainda pendente). */
  latestTask?: fhir5.Task;
}

/** Convite mais recente de cada Appointment (pelo `identifier` do Appointment). */
export function latestInviteByAppointment(tasks: fhir5.Task[]): Map<string, fhir5.Task> {
  const latest = new Map<string, fhir5.Task>();
  for (const task of tasks) {
    const appointmentId = inviteAppointmentId(task);
    if (!appointmentId) continue;
    const current = latest.get(appointmentId);
    if (!current || dayjs(task.authoredOn).isAfter(dayjs(current.authoredOn))) {
      latest.set(appointmentId, task);
    }
  }
  return latest;
}

/** Status da reserva a partir do Appointment (CON) e do último convite. */
export function bookingInviteState(
  appointment: fhir5.Appointment,
  latestTask: fhir5.Task | undefined,
  now: Dayjs = dayjs(),
): BookingInviteState {
  const guest = teleconsultoriaGuestOfAppointment(appointment).practitioner;
  const invitedName = latestTask?.for?.display || "";
  if (guest) {
    return { status: "accepted", label: "Aceita", teleconsultorName: guest.display, latestTask };
  }
  if (latestTask?.status === "rejected") {
    return { status: "rejected", label: "Recusada", teleconsultorName: invitedName, latestTask };
  }
  if (latestTask?.status === "cancelled") {
    return { status: "cancelled", label: "Convite cancelado", teleconsultorName: invitedName, latestTask };
  }
  if (latestTask && isInviteExpired(latestTask, now)) {
    return { status: "expired", label: "Expirada", teleconsultorName: invitedName, latestTask };
  }
  return {
    status: "pending",
    label: invitedName ? `Aguardando aceite de ${invitedName}` : "Aguardando aceite",
    teleconsultorName: invitedName,
    latestTask,
  };
}
